import { useState, useMemo } from "react";
import {
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Popover,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid-premium";

interface ShowHideColumnsPanelProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  columns: GridColDef[];
  columnVisibilityModel: Record<string, boolean>;
  onToggleColumn: (field: string, visible: boolean) => void;
  onToggleAll: (visible: boolean) => void;
  onReset: () => void;
}

// Fields that should never appear in the show/hide panel
const EXCLUDED_FIELDS = new Set(["actions", "__detail_panel_toggle__"]);

export function ShowHideColumnsPanel({
  anchorEl,
  onClose,
  columns,
  columnVisibilityModel,
  onToggleColumn,
  onToggleAll,
  onReset,
}: ShowHideColumnsPanelProps) {
  const [searchText, setSearchText] = useState("");

  // Filter columns: exclude internal fields and non-hideable columns
  const manageableColumns = useMemo(
    () =>
      columns.filter(
        (col) => !EXCLUDED_FIELDS.has(col.field) && col.hideable !== false
      ),
    [columns]
  );

  // Filter by search text
  const filteredColumns = useMemo(() => {
    if (!searchText.trim()) return manageableColumns;
    const lower = searchText.toLowerCase();
    return manageableColumns.filter((col) => {
      const label = col.headerName || col.field;
      return label.toLowerCase().includes(lower);
    });
  }, [manageableColumns, searchText]);

  // Check if a column is visible (default is true if not in the model)
  const isColumnVisible = (field: string): boolean => {
    return columnVisibilityModel[field] !== false;
  };

  // Determine "all visible" state for the toggle-all checkbox
  const allVisible = manageableColumns.every((col) =>
    isColumnVisible(col.field)
  );
  const someVisible =
    !allVisible && manageableColumns.some((col) => isColumnVisible(col.field));

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            width: 320,
            display: "flex",
            flexDirection: "column",
            maxHeight: "none",
          },
        },
      }}
    >
      {/* Header with search */}
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Manage Columns
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search columns..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <Divider />

      {/* Scrollable column list - show 10 items at a time (~40px per item) */}
      <List
        dense
        sx={{
          maxHeight: 400,
          overflowY: "auto",
          py: 0,
        }}
      >
        {filteredColumns.length === 0 ? (
          <ListItem>
            <ListItemText
              primary={
                <Typography variant="body2" color="text.secondary">
                  No columns match your search
                </Typography>
              }
            />
          </ListItem>
        ) : (
          filteredColumns.map((col) => {
            const visible = isColumnVisible(col.field);
            const label = col.headerName || col.field;
            return (
              <ListItem key={col.field} disablePadding>
                <ListItemButton
                  dense
                  onClick={() => onToggleColumn(col.field, !visible)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={visible}
                      size="small"
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={label} />
                </ListItemButton>
              </ListItem>
            );
          })
        )}
      </List>

      <Divider />

      {/* Bottom bar: Show/Hide All + Reset */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={allVisible}
              indeterminate={someVisible}
              size="small"
              onChange={() => onToggleAll(!allVisible)}
            />
          }
          label={<Typography variant="body2">Show/hide all</Typography>}
        />
        <Typography
          variant="body2"
          onClick={onReset}
          sx={{
            color: "primary.main",
            cursor: "pointer",
          }}
          title="Reset to default column visibility"
        >
          Reset
        </Typography>
      </Box>
    </Popover>
  );
}
